import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { productList } from "@/data/products";
import Logo from "@/components/Logo";
import { subscribeBeta } from "@/lib/subscribe";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeBeta(email, "footer");
      toast.success("You're in. We'll be in touch when beta opens.");
      setEmail("");
    } catch (err) {
      toast.error(err?.message || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-slate-200 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-2">
            <Link to="/" className="w-fit">
              <Logo />
            </Link>
            <p className="mt-4 max-w-md text-slate-600 leading-relaxed">
              Cognitive-first learning tools, engineered as agentic loops. Join
              the newsletter for product drops and research notes.
            </p>

            <form
              onSubmit={handleSubscribe}
              data-testid="footer-newsletter-form"
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="footer-newsletter-input"
            className="flex-1 px-4 py-3 rounded-full bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100 shadow-sm shadow-orange-500/20"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Subscribe <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Products links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              Our Apps
            </h4>
            <ul className="mt-5 space-y-3">
              {productList.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/${p.slug}`}
                    data-testid={`footer-link-${p.slug}`}
                    className="group inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <span className={`h-2 w-2 rounded-full ${p.theme.dot}`} />
                    <span className="font-medium">{p.name}</span>
                    <span className="text-slate-400 text-sm">
                      {p.comingSoon ? p.comingSoonLabel : p.tagline}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} CognitLoop. One-to-one tutors that stay
            until you can prove it.
          </p>
          <p className="text-sm text-slate-400">
            Built for cognitive-first education.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
